import { Component, Inject, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { CrmObjectInterface } from '@vendasta/crm';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatButton } from '@angular/material/button';
import { AssociationType } from '../../../constants';
import { EditAssociationTagsComponent } from '../edit-association-tags/edit-association-tags.component';
import { TranslateByObjectTypePipe, TranslationModule } from '../../../i18n';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { ObjectType } from '../../../tokens-and-interfaces';
import { CrmObjectDisplayService } from '../../../shared-services';

export interface SelectPrimaryAssociationDialogData {
  selectedObjects: CrmObjectInterface[];
  associationType: AssociationType;
  objectType: ObjectType;
}

export interface SelectPrimaryAssociationDialogResult {
  continue: boolean;
  tagMap: Map<string, string[]>;
}

@Component({
  selector: 'crm-select-primary-association-modal',
  templateUrl: './select-primary-association-modal.component.html',
  styleUrls: ['./select-primary-association-modal.component.scss'],
  imports: [
    MatDialogTitle,
    MatDialogContent,
    GalaxyFormFieldModule,
    EditAssociationTagsComponent,
    MatDialogActions,
    TranslationModule,
    MatButton,
    MatRadioModule,
    FormsModule,
    TranslateByObjectTypePipe,
  ],
})
export class SelectPrimaryAssociationModalComponent {
  protected readonly tagMap = signal(new Map<string, string[]>());
  protected readonly associations = this.data.selectedObjects;
  protected readonly objectType = this.data.objectType;
  protected readonly primaryAssociation = signal(this?.associations[0]?.crmObjectId ?? '');
  protected readonly displayNamesByObjectId = new Map<string, string>();

  constructor(
    public dialogRef: MatDialogRef<SelectPrimaryAssociationModalComponent, SelectPrimaryAssociationDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: SelectPrimaryAssociationDialogData,
    private readonly objectDisplayService: CrmObjectDisplayService,
  ) {
    this.data.selectedObjects.forEach((object) => {
      this.displayNamesByObjectId.set(
        object.crmObjectId ?? '',
        this.objectDisplayService.getObjectDisplayName(this.objectType, object),
      );
    });
  }

  tagsChanged(objectId: string, tags: string[]): void {
    this.tagMap.update((prev) => {
      return prev.set(objectId, tags);
    });
  }

  setPrimaryAssociation(objectId: string): void {
    this.primaryAssociation.set(objectId);
  }

  cancel(): void {
    this.dialogRef.close({ continue: false, tagMap: this.tagMap() } as SelectPrimaryAssociationDialogResult);
  }

  save(): void {
    const primaryId = this.primaryAssociation();
    const tagMap = this.tagMap();
    const prevTags = tagMap.get(primaryId) ?? [];
    tagMap.set(primaryId, ['Primary', ...prevTags]);
    this.dialogRef.close({ continue: true, tagMap: tagMap } as SelectPrimaryAssociationDialogResult);
  }
}
